import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Background, Controls, MiniMap, ReactFlow, Node, Edge, MarkerType, Position, useNodesState, useEdgesState, Handle } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CommitmentDetail, getCommitmentById } from '@/api/commitment';
import { SupportedChainId, SupportedChainMap, SupportedChains } from '@packet/constants/AppSupportChains';
import { truncateAddress } from '@/utils/address';
import AssetAmount from '@/components/AssetView/AssetAmount';
import AssetSymbol from '@/components/AssetView/AssetSymbol';
import styled from 'styled-components';
import { Hash } from 'viem';
import { TransactionBaseDto, TransactionType, TransactionTypeName } from '@/types/commitment';
import { timeFmt } from '@/utils/timeFmt';

const FlowContainer = styled.div`
  width: 100%;
  height: 600px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
`;

const NodeContent = styled.div`
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60px;
  background: white;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  text-align: center;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;

  &.current {
    border-color: #1890ff;
    background: #e6f7ff;
    font-weight: bold;
  }

  &.is-active-true {
    border-color: #52c41a;
    background: #f6ffed;
  }

  &.child {
    border-color: #fa541c;
    background: #fff2e8;
  }

  &.sibling {
    border-color: #722ed1;
    background: #f9f0ff;
  }
`;

const EvmNodeContent = styled.div`
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60px;
  background: #ddd;
  border: 2px solid #e8e8e8;
  border-radius: 80px;
  text-align: center;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CommitmentTag = styled.div`
  border-radius: 12px;
  padding: 2px 6px;
  font-size: 10px;
  color: #141413;
  position: absolute;
  left: -2px;
  top: -2px;
`;
const NodeTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: #262626;
`;

const NodeAmount = styled.div`
  color: #1890ff;
  font-weight: 500;
`;

const TransactionBox = styled.div`
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 40px;
  background: #fff7e6;
  border: 1px solid #ffd666;
  border-radius: 6px;
  font-size: 10px;
  color: #d48806;
  font-weight: 500;
`;

const UserBox = styled.div`
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 40px;
  background: #ebffe6;
  border: 1px solid #7dff66;
  border-radius: 6px;
  font-size: 10px;
  color: #06d44e;
  font-weight: 500;
`;

const EvmUserBox = styled.div`
  width: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 40px;
  background: #ebffe6;
  border: 1px solid #7dff66;
  border-radius: 60px;
  font-size: 10px;
  color: #06cdd4;
  font-weight: 500;
`;

// 自定义节点组件
const CommitmentNode = ({ data }: { data: { focus?: CommitmentDetail; commitment: CommitmentDetail; chainId: SupportedChainId } }) => {
  const cmt = data.commitment;
  return (
    <NodeContent className={`is-active-${cmt.commitment === data.focus?.commitment ? 'true' : 'false'}`}>
      <Handle type="target" position={Position.Top} id="top" style={{ background: '#555' }} />
      <Handle type="target" position={Position.Left} id="user" style={{ background: '#555' }} />
      <CommitmentTag>UTXO</CommitmentTag>
      <NodeTitle>{truncateAddress(cmt.commitment, 6, 4)}</NodeTitle>
      {cmt.amount && (
        <NodeAmount>
          <AssetAmount amount={cmt.amount} assetId={cmt.asset} chainId={data.chainId} /> <AssetSymbol assetId={cmt.asset} chainId={data.chainId} />
        </NodeAmount>
      )}
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ background: '#555' }} />
    </NodeContent>
  );
};

const EvmCommitmentNode = ({ data }: { data: { amount: Hash; evm_address: Hash; asset: Hash; chainId: SupportedChainId } }) => {
  const chain = SupportedChainMap[data.chainId] || SupportedChains[0];
  return (
    <EvmNodeContent>
      <Handle type="target" position={Position.Top} id="top" style={{ background: '#555' }} />
      <Handle type="target" position={Position.Left} id="user" style={{ background: '#555' }} />
      <NodeTitle>{chain.name}</NodeTitle>
      {data.amount && (
        <NodeAmount>
          <AssetAmount amount={data.amount} assetId={data.asset} chainId={data.chainId} /> <AssetSymbol assetId={data.asset} chainId={data.chainId} />
        </NodeAmount>
      )}
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ background: '#555' }} />
    </EvmNodeContent>
  );
};

// 自定义交易节点组件
const TransactionNode = ({ data }: { data: { transaction: TransactionBaseDto } }) => {
  const txId = `${data.transaction.txhash}-${data.transaction.tx_index}`;
  return (
    <TransactionBox>
      <Handle type="target" position={Position.Top} id="top" style={{ background: '#555' }} />
      {TransactionTypeName[data.transaction.tx_type]} Transaction
      <br />
      <NodeTitle>{truncateAddress(txId, 6, 4)}</NodeTitle>
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ background: '#555' }} />
    </TransactionBox>
  );
};

const AppAddressNode = ({ data }: { data: { user_pk: Hash } }) => {
  return (
    <UserBox>
      Ocash Address
      <br />
      {truncateAddress(data.user_pk, 4, 4)}
      <Handle type="target" position={Position.Right} id="target" style={{ background: '#555' }} />
      <Handle type="source" position={Position.Right} id="right" style={{ background: '#555' }} />
    </UserBox>
  );
};

const EvmAddressNode = ({ data }: { data: { evm_address: Hash; chainId: SupportedChainId } }) => {
  const chain = SupportedChainMap[data.chainId] || SupportedChains[0];
  return (
    <EvmUserBox>
      EVM Address
      <br />
      {truncateAddress(data.evm_address, 4, 4)}
      <Handle type="target" position={Position.Right} id="target" style={{ background: '#555' }} />
      <Handle type="source" position={Position.Right} id="right" style={{ background: '#555' }} />
    </EvmUserBox>
  );
};

const nodeTypes = {
  commitmentNode: CommitmentNode,
  evmCommitmentNode: EvmCommitmentNode,
  transactionNode: TransactionNode,
  appAddressNode: AppAddressNode,
  evmAddressNode: EvmAddressNode,
};

interface CommitmentFlowProps {
  commitment: CommitmentDetail;
  chainId: SupportedChainId;
}
/**
 * Flow 布局配置
 */
const viewSize = {
  beginX: 0, // 流程图开始X坐标
  beginY: 0, // 流程图开始Y坐标
  diffX: 200, // 两个节点之间的X轴距离
  diffY: 150, // 两个节点之间的Y轴距离

  userItemLeft: 700, // 用户节点往左移动距离
  userEvmItemLeft: 500, // 用户节点往左移动距离
  nextCommitmentDiffTop: 50, // 同一行之间不同 节点 之间 y轴间隔
};

/**
 * 绘制 commitment 的家族关系图
 */
const CommitmentFlow: React.FC<CommitmentFlowProps> = ({ commitment, chainId }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [hoveredUserNode, setHoveredUserNode] = useState<string | null>(null);
  const cmtCache = useRef<Record<string, CommitmentDetail>>({});
  /**
   * 目前 所有 nodes 数据
   * 目前 所有 edges 数据
   * 目前 所有 positions 数据 标记这些位置已经被使用
   */
  const mappingRef = useRef<{ nodes: Record<string, Node>; edges: Record<string, Edge>; positions: Record<string, boolean> }>({ nodes: {}, edges: {}, positions: {} });

  const loadCommitmentFamily = async (loadCmt: CommitmentDetail, beginX: number, beginY: number) => {
    cmtCache.current[loadCmt.commitment] = loadCmt;
    const mapping = mappingRef.current;
    // 找出 和当前 commitment 上下关联的 commitment （父/兄/子）
    const parents = loadCmt.from?.[0]?.inputs || [];
    const siblings1 = loadCmt.from?.[0]?.outputs || [];
    const siblings2 = loadCmt.to?.[0]?.inputs || [];
    const children = loadCmt.to?.[0]?.outputs || [];
    let waitToLoadIds = [...parents, ...siblings1, ...siblings2, ...children];
    waitToLoadIds = Array.from(new Set(waitToLoadIds));
    waitToLoadIds = waitToLoadIds.filter((id) => !cmtCache.current[id]); // 过滤掉已加载的 commitments
    if (waitToLoadIds.length > 0) {
      const reqs = await getCommitmentById(Array.from(waitToLoadIds));
      reqs.data?.forEach((item) => {
        if (!item) return;
        cmtCache.current[item.commitment] = item;
      });
    }

    const transactions = [loadCmt.from?.[0], loadCmt.to?.[0]];
    const positionView = { x: beginX, y: beginY + viewSize.diffY };
    const getPosition = (x: number, y: number, isLeftItem = false) => {
      const key = `${x}-${y}`;
      if (mapping.positions[key]) return getPosition(x + (isLeftItem ? -viewSize.diffX : viewSize.diffX), y + -viewSize.nextCommitmentDiffTop, isLeftItem);
      mapping.positions[key] = true;
      return { x, y };
    };
    const getTxId = (tx: TransactionBaseDto<Hash>) => `tx-${tx.txhash}-${tx.tx_index}`;
    const getCmtId = (commitment: Hash) => `cmt-${commitment}`;
    const getAppAddressId = (user_pk: string) => `user-pk-${user_pk}`;
    const getEvmAssetId = (evm_address: string, tx: TransactionBaseDto<Hash>) => `evm-${evm_address}-${tx.txhash}-${tx.tx_index}`;
    const getEvmAddressId = (evm_address: string) => `evm-${evm_address}`;
    const getConnId = (data: { source: string; target: string }) => `conn-${data.source}-${data.target}`;

    // return false(未添加 conn)， true（添加了一条 conn）
    const addEdgeWhileNotExists = (data: { source: string; target: string } & Partial<Edge>): boolean => {
      const edgeId = getConnId(data);
      if (mapping.edges[edgeId]) return false;
      mapping.edges[edgeId] = { id: edgeId, ...data };
      addEdges.push(mapping.edges[edgeId]);
      return true;
    };

    const addNodeWhileNotExists = (data: Node, getPosition: () => { x: number; y: number }): boolean => {
      const nodeId = data.id;
      if (mapping.nodes[nodeId]) return false;
      const pos = getPosition();
      mapping.nodes[nodeId] = { ...data, position: pos };
      addNodes.push(mapping.nodes[nodeId]);
      return true;
    };
    const addNodes: Node[] = [];
    const addEdges: Edge[] = [];

    // 获取正常 资产节点的位置
    const getPos = () => getPosition(positionView.x, positionView.y);

    // 获取 用户节点 的位置
    const getAddressPos = () => getPosition(positionView.x - viewSize.userItemLeft, positionView.y, true);

    // 获取 evm用户节点 的位置
    const getEvmAddressPos = () => getPosition(positionView.x - viewSize.userEvmItemLeft, positionView.y, true);
    const getEvmAssetPos = () => getPosition(positionView.x, positionView.y, true);

    const searchNodeEdge = (cmts: Hash[] | TransactionBaseDto<`0x${string}`>) => {
      let hasNodeAdded = false;

      // 添加交易节点
      if (!Array.isArray(cmts)) {
        const txId = getTxId(cmts);
        const added = addNodeWhileNotExists({ id: txId, type: 'transactionNode', position: { x: 0, y: 0 }, data: { transaction: cmts } }, getPos);
        if (added) hasNodeAdded = true;
        return { hasNodeAdded };
      }
      cmts.forEach((id) => {
        const data = cmtCache.current[id];
        if (!data) return;
        const cmtId = getCmtId(id);
        const addedCmt = addNodeWhileNotExists({ id: cmtId, type: 'commitmentNode', position: { x: 0, y: 0 }, data: { commitment: data, focus: commitment, chainId } }, getPos);
        if (addedCmt) hasNodeAdded = true;

        // 添加用户节点
        const uId = getAppAddressId(data.user_pk);
        addNodeWhileNotExists({ id: uId, type: 'appAddressNode', position: { x: 0, y: 0 }, data: { user_pk: data.user_pk } }, getAddressPos);

        // 添加用户和节点的关系
        addEdgeWhileNotExists({ source: uId, target: cmtId, targetHandle: 'user', style: { stroke: '#d9d9d9', strokeDasharray: '5,8', strokeWidth: 1 } });
      });
      return { hasNodeAdded };
    };
    const siblings = [...siblings1, ...siblings2];
    const arr = [
      // 父级
      parents,
      // 父级于当前 资产的 交易记录
      loadCmt.from?.[0],
      // 兄弟节点
      siblings,
      // 产生 子 节点的交易记录
      loadCmt.to?.[0],
      // 所产生的 子 节点
      children,
    ];
    arr.forEach((group, index) => {
      if (!group) return;

      let { hasNodeAdded } = searchNodeEdge(group);
      const from = loadCmt.from?.[0];
      const to = loadCmt.to?.[0];

      const addEvmAsset = (evm_address: Hash, amount: Hash, tx: TransactionBaseDto<Hash>, isNodeOnTop: boolean) => {
        const evmAssetId = getEvmAssetId(evm_address, tx);

        const added = addNodeWhileNotExists({ id: evmAssetId, type: 'evmCommitmentNode', position: { x: 0, y: 0 }, data: { evm_address, amount, asset: loadCmt.asset, chainId } }, getEvmAssetPos);
        if (added) hasNodeAdded = true;

        const evmId = getEvmAddressId(evm_address);
        addNodeWhileNotExists({ id: evmId, type: 'evmAddressNode', position: { x: 0, y: 0 }, data: { evm_address, chainId } }, getEvmAddressPos);
        let source = getTxId(tx);
        let target = evmAssetId;
        if (isNodeOnTop) {
          target = source;
          source = evmAssetId;
        }
        addEdgeWhileNotExists({ source, target, markerEnd: { type: MarkerType.Arrow }, label: timeFmt(tx.created_at) });
        addEdgeWhileNotExists({ source: evmId, target: evmAssetId, targetHandle: 'user', label: 'owner' });
      };

      // 父级结算完成 需要补充 可能的 deposit evm 资产节点
      if (group === parents && from && from.tx_type === TransactionType.Deposit) {
        if (from.evm_address && from.amount) {
          addEvmAsset(from.evm_address, from.amount, from, true);
        }
      }

      // 兄弟节点结算完成 需要补充 可能的 withdraw evm 资产节点
      if (group === siblings && from && from.tx_type === TransactionType.Withdraw) {
        if (from.evm_address && from.amount) {
          addEvmAsset(from.evm_address, from.amount, from, false);
        }
      }

      // 子节点结算完 需要补充 可能的 withdraw evm 资产节点
      if (group === children && to && to.tx_type === TransactionType.Withdraw) {
        if (to.evm_address && to.amount) {
          addEvmAsset(to.evm_address, to.amount, to, false);
        }
      }
      if (hasNodeAdded) {
        positionView.y = positionView.y + viewSize.diffY * 1;
      }
      positionView.x = beginX;
    });

    // 从交易分析出 commitment 和 tx 的关联关系
    transactions.forEach((tx) => {
      if (!tx) return;
      const inputs = tx.inputs || [];
      const outputs = tx.outputs || [];
      const txId = getTxId(tx);
      inputs.forEach((input) => {
        const source = getCmtId(input);
        addEdgeWhileNotExists({ source, target: txId, animated: true, markerEnd: { type: MarkerType.Arrow }, label: timeFmt(tx.created_at) });
      });
      outputs.forEach((output) => {
        const target = getCmtId(output);
        addEdgeWhileNotExists({ source: txId, target, animated: true, markerEnd: { type: MarkerType.Arrow }, label: timeFmt(tx.created_at) });
      });
    });

    setNodes((prevNodes) => [...prevNodes, ...addNodes]);
    setEdges((prevEdges) => [...prevEdges, ...addEdges]);
  };

  useEffect(() => {
    loadCommitmentFamily(commitment, viewSize.beginX, viewSize.beginY);
  }, [commitment]);

  // 更新连接线的高亮样式
  useEffect(() => {
    if (hoveredUserNode) {
      setEdges((prevEdges) => {
        return prevEdges.map((edge) => {
          if ([edge.source, edge.target].includes(hoveredUserNode)) {
            const edgeData = edge as any;
            if (edgeData._bakStyle) return edge; // 已经高亮
            return { ...edge, _bakStyle: { ...edge.style }, style: { ...edge.style, stroke: '#1890ff', strokeWidth: 3, strokeDasharray: '8,5' } };
          }
          return edge;
        });
      });
    } else {
      setEdges((prevEdges) => {
        const ress = prevEdges.map((edge) => {
          if ((edge as any)._bakStyle) {
            const newEdge = { ...edge, style: { ...(edge as any)._bakStyle } };
            delete (newEdge as any)._bakStyle;
            return newEdge;
          }
          return edge;
        });
        console.log('ress', ress);
        return ress;
      });
    }
  }, [hoveredUserNode, setEdges]);

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (node.type === 'commitmentNode' && node.id !== commitment.commitment) {
        // 可以在这里添加点击节点的逻辑，比如导航到对应的UTXO详情页
        console.log('Clicked node:', node.id);
      }
    },
    [commitment.commitment],
  );

  const onNodeMouseEnter = useCallback((event: React.MouseEvent, node: Node) => {
    setHoveredUserNode(node.id);
  }, []);

  const onNodeMouseLeave = useCallback((event: React.MouseEvent, node: Node) => {
    setHoveredUserNode(null);
  }, []);

  const onEdgeMouseEnter = useCallback((event: React.MouseEvent, edge: Edge) => {
    const userNodeId = edge.source;
    setHoveredUserNode(userNodeId);
  }, []);

  const onEdgeMouseLeave = useCallback((event: React.MouseEvent, edge: Edge) => {
    setHoveredUserNode(null);
  }, []);

  return (
    <FlowContainer>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        fitView
        fitViewOptions={{ padding: 0 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </FlowContainer>
  );
};

export default CommitmentFlow;
